import React from 'react';
import { Button, Box, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components-core/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components-core/others/AlertDialogContainer';
import { submitResortAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components-core/ConfirmDialog';

export const ResortForm = props => {
  const { data, submitCallback, handleClose, allSeasons } = props;

  const { loading, mutate } = useMutation(submitResortAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste')
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    address: data ? data.address || '' : '',
    reply_email: data ? data.reply_email || '' : '',
    hidden_copy_email: data ? data.hidden_copy_email || '' : '',
    membership_fee: data ? data.membership_fee || '' : ''
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano ośrodek' : 'Dodano nowy ośrodek', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego ośrodka');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto ośrodek ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć ośrodka ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa ośrodka"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Adres ośrodka</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="address"
              id="address"
              type="text"
              placeholder="adres ośrodka"
              value={formik.values.address}
              onChange={formik.handleChange}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Email do odpowiedzi</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="reply_email"
              id="reply_email"
              type="text"
              placeholder="email do odpowiedzi"
              value={formik.values.reply_email}
              onChange={formik.handleChange}
              error={formik.touched.reply_email && Boolean(formik.errors.reply_email)}
              helperText={formik.touched.reply_email && formik.errors.reply_email}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Email do wysyłania ukrytej kopii</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="hidden_copy_email"
              id="hidden_copy_email"
              type="text"
              placeholder="email do wysyłania ukrytej kopii"
              value={formik.values.hidden_copy_email}
              onChange={formik.handleChange}
              error={formik.touched.hidden_copy_email && Boolean(formik.errors.hidden_copy_email)}
              helperText={formik.touched.hidden_copy_email && formik.errors.hidden_copy_email}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Składka członkowska (zł)</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="membership_fee"
              id="membership_fee"
              type="text"
              placeholder="Składka członkowska"
              value={formik.values.membership_fee}
              onChange={formik.handleChange}
              error={formik.touched.membership_fee && Boolean(formik.errors.membership_fee)}
              helperText={formik.touched.membership_fee && formik.errors.membership_fee}
              debounceTimeout={300}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć Zamawiającego?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć ten ośrodek?
      </ConfirmDialog>
    </>

  );
};
